import { useState, useEffect, useRef, ChangeEvent } from 'react';

import {
  Box,
  Input,
  InputGroup,
  useDisclosure,
  useOutsideClick,
  InputLeftElement,
  Text,
  Divider,
  HStack,
} from '@chakra-ui/react';

import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';

import { countries } from 'countries-list';

import CountrySearchList from 'app/components/UI/Inputs/PhoneNumberInput/CountrySearchList';
import { CountryProps } from 'app/components/UI/Inputs/PhoneNumberInput/CountrySearchList/types';

import { ArrowDownBold, ArrowUpBold } from '@aslanpay/portal-common-lib';

import { PhoneNumberInputProps } from './types';

function PhoneNumberInput({
  defaultValue = '',
  placeholder = 'Enter work or personal number',
  isDisabled = false,
  onChange,
  onBlur,
}: PhoneNumberInputProps) {
  const ref = useRef<HTMLDivElement>(null);

  const initialParsedNumber = parsePhoneNumberFromString(defaultValue);

  const [number, setNumber] = useState<string>(initialParsedNumber?.nationalNumber || defaultValue);
  const [countryDialCode, setCountryDialCode] = useState<string>(
    `+${initialParsedNumber?.countryCallingCode || '44'}`
  );
  const [countryFlag, setCountryFlag] = useState<string>(
    countries[(initialParsedNumber?.country as keyof typeof countries) || 'GB'].emoji
  );
  const { isOpen, onToggle, onClose } = useDisclosure();

  useOutsideClick({
    ref,
    handler: () => onClose(),
  });

  useEffect(() => {
    setCountryFlag(countryFlag);
    setCountryDialCode(countryDialCode);
  }, [countryFlag, countryDialCode]);

  const onCountryChange = (item: CountryProps) => {
    const parsedNumber = new AsYouType().input(`+${item.details.phone}${number}`);

    setCountryDialCode(`+${item.details.phone}`);
    setCountryFlag(item?.details.emoji);

    onChange(number.length ? parsedNumber : number);
    onClose();
  };

  const onPhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const parsedNumber = new AsYouType().input(`${countryDialCode}${value}`);

    const numberNoLetters = value.replace(/\D/g, '');

    if (numberNoLetters === value) {
      setNumber(value);

      onChange(value.length ? parsedNumber : value);
    }
  };

  return (
    <Box ref={ref} position="relative">
      <InputGroup>
        <InputLeftElement
          w="86px"
          h="full"
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          onClick={isDisabled ? undefined : onToggle}
          opacity={isDisabled ? '0.4' : '1'}
        >
          <HStack spacing="7px" height="32px">
            <Text fontSize="22px">{countryFlag}</Text>
            {isOpen ? (
              <ArrowUpBold color="brand.darkGray" boxSize="18px" />
            ) : (
              <ArrowDownBold color="brand.darkGray" boxSize="18px" />
            )}
            <Divider orientation="vertical" />
          </HStack>
        </InputLeftElement>
        <Input
          size="lg"
          pl="80px"
          type="tel"
          value={number}
          placeholder={placeholder}
          onChange={onPhoneNumberChange}
          onBlur={onBlur}
        />
      </InputGroup>

      {isOpen ? <CountrySearchList onChange={onCountryChange} /> : null}
    </Box>
  );
}

export default PhoneNumberInput;
